<template>
  <div
    class="insurance-verification-huk-section"
    data-qa="insurance-verification-huk-section"
  >
    <div
      class="insurance-verification-huk-section__title"
      data-qa="title"
      v-html="$t('eu__insurance_verification__private__header__huk')"
    />
    <div
      class="insurance-verification-huk-section__title insurance-verification-huk-section__title--regular"
      data-qa="description"
      v-html="$t('eu__insurance_verification__private__description__huk')"
    />
    <div
      v-if="!error"
      class="insurance-verification-huk-section__loader"
      data-qa="loader"
    >
      <Base64Spinner />
    </div>
    <div
      v-else
      class="insurance-verification-huk-section__error"
    >
      <div
        class="insurance-verification-huk-section__title insurance-verification-huk-section__title--error insurance-verification-huk-section__title--last"
        data-qa="error-title"
        v-html="error.title"
      />
      <div
        class="insurance-verification-huk-section__title insurance-verification-huk-section__title--error insurance-verification-huk-section__title--regular"
        data-qa="error-description"
        v-html="error.description"
      />
      <BaseButton
        :text="$t('generic__try_again')"
        data-qa="huk-verification-retry"
        @click="$emit('retry')"
      />
    </div>
  </div>
</template>

<script setup>
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import { subscribeWithInsuranceToken } from '@shared/api-endpoints'
import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink'
import env from '@shared/env.js'
import { onMounted, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'

const { t } = useI18n()
defineEmits(['retry'])

const props = defineProps({
  type: {
    type: String,
    default: '',
  },
  token: {
    type: String,
    default: '',
  },
  corporate: {
    type: String,
    default: '',
  },
  user: {
    type: Object,
    default: () => Object.create(null),
  },
})

const error = ref(null)

/*
    links to test the flow:
    start desktop:
      http://localhost:8080/b2b?corporate=huk&from_app=false
    start mobile:
      http://localhost:8080/b2b?corporate=huk&from_app=true
    subscribe invalid:
      http://localhost:8080/b2b?corporate=huk&token=testtokeninvalid&type=init
    subscribe valid -> signup:
      http://localhost:8080/b2b?corporate=huk&token=testtokenvalid&type=init&login=false
    subscribe valid -> login:
      http://localhost:8080/b2b?corporate=huk&token=testtokenvalid&type=init&login=true
    */

onMounted(async () => {
  try {
    await subscribeWithInsuranceToken(props.corporate, props.token, props.type)

    // TODO use download link screen
    window.location.redirectToDeeplink(
      await assembleKaiaAppDeepLink({
        path: 'auth/one_time_token',
        addOneTimeAuthToken: true,
        fallback: () =>
          assembleKaiaAppDeepLink({
            path: 'goto/screen',
            query: { target: 'login' },
          }),
      }),
    )
  } catch (e) {
    // handle incorrect token and type;
    error.value = {
      title: t('b2b__sponsor__label__activation_failed'),
      description: t(
        'b2b__sponsor__label__kaia_pro_failed_to_activate_with_sponsor_huk_with_support_email',
        { VUE_APP_SUPPORT_EMAIL: env.VITE_SUPPORT_EMAIL },
      ),
    }

    console.error(
      new Error(
        `HUK token validation failed with error code ${e.response?.status}`,
      ),
    )
  }
})
</script>

<style lang="scss">
.insurance-verification-huk-section {
  width: 100%;
  box-sizing: border-box;

  &__title {
    color: $kds-color-text-primary;
    margin-bottom: 16px;

    @include kds-typography-title-medium;

    &--regular {
      font-weight: 400;
    }

    &--last {
      margin-bottom: 0;
    }

    &--error {
      color: $kds-color-text-interactive-destructive;
    }
  }

  &__loader {
    margin-bottom: -10px;
    width: 60px;
    height: 60px;
  }

  &__error {
    margin-top: 16px;
    margin-bottom: -16px;
  }
}
</style>
